import React, { useState, useEffect } from "react";

const CircleAreaTransformation = () => {
  const [radius, setRadius] = useState(100);
  const [sides, setSides] = useState(6);
  const [step, setStep] = useState(0);
  const [showTriangles, setShowTriangles] = useState(false);
  const [showFormula, setShowFormula] = useState(false);
  const [showRearrangedTriangles, setShowRearrangedTriangles] = useState(false);

  // 计算面积和周长
  const area = Math.PI * radius * radius;
  const circumference = 2 * Math.PI * radius;

  // 创建正多边形的点
  const generatePolygonPoints = (sides) => {
    const points = [];
    for (let i = 0; i < sides; i++) {
      const angle = (i * 2 * Math.PI) / sides;
      points.push({
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      });
    }
    return points;
  };

  const polygonPoints = generatePolygonPoints(sides);

  // SVG路径
  const circlePath = `M ${radius + 0} ${0} A ${radius} ${radius} 0 1 0 ${
    radius - 0
  } ${0} A ${radius} ${radius} 0 1 0 ${radius + 0} ${0}`;

  const polygonPath =
    polygonPoints
      .map((point, index) => {
        return `${index === 0 ? "M" : "L"} ${point.x + radius} ${
          point.y + radius
        }`;
      })
      .join(" ") + " Z";

  // 计算三角形
  const triangles = polygonPoints.map((point, index) => {
    return {
      points: `${radius},${radius} ${point.x + radius},${point.y + radius} ${
        polygonPoints[(index + 1) % sides].x + radius
      },${polygonPoints[(index + 1) % sides].y + radius}`,
    };
  });

  // 设置步骤描述
  const stepDescriptions = [
    "这是一个圆，我们要找出它的面积",
    "我们可以把圆近似为一个正多边形",
    "正多边形可以分成许多三角形",
    "每个三角形的底是多边形的一条边，高是半径",
    "当多边形的边数增加，它越来越接近圆",
    "我们可以把这些三角形重新排列",
    "最终，圆的面积等于πr²，相当于周长(2πr)×半径(r)÷2",
  ];

  return (
    <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg shadow-md">
      <h1 className="text-2xl font-bold text-blue-800 mb-4">圆面积转化演示</h1>

      <div className="mb-6 text-lg font-semibold text-center text-blue-600">
        {stepDescriptions[step]}
      </div>

      <div className="relative mb-6">
        <svg
          width={radius * 2 + 40}
          height={radius * 2 + 40}
          className="bg-white rounded-lg shadow-sm"
        >
          {step === 0 && (
            <circle
              cx={radius + 20}
              cy={radius + 20}
              r={radius}
              fill="rgba(173, 216, 230, 0.5)"
              stroke="blue"
              strokeWidth="2"
            />
          )}

          {step >= 1 && step < 5 && (
            <path
              d={polygonPath}
              transform={`translate(20, 20)`}
              fill="rgba(173, 216, 230, 0.5)"
              stroke="blue"
              strokeWidth="2"
            />
          )}

          {(step === 5 || step === 6) && !showRearrangedTriangles && (
            <circle
              cx={radius + 20}
              cy={radius + 20}
              r={radius}
              fill="rgba(173, 216, 230, 0.5)"
              stroke="blue"
              strokeWidth="2"
            />
          )}

          {step === 5 && showRearrangedTriangles && (
            <g>
              {triangles.map((triangle, index) => {
                // 计算每个三角形在直线上的位置
                const segmentLength = (2 * Math.PI * radius) / sides;
                const startX = 20;
                const startY = radius * 2;

                return (
                  <polygon
                    key={index}
                    points={`${startX + index * segmentLength},${startY} ${
                      startX + (index + 1) * segmentLength
                    },${startY} ${startX + (index + 0.5) * segmentLength},${
                      startY - radius
                    }`}
                    fill={`hsl(${(index * 360) / sides}, 70%, 80%)`}
                    stroke="rgba(0,0,0,0.3)"
                    strokeWidth="1"
                  />
                );
              })}
              <line
                x1={20}
                y1={radius * 2}
                x2={20 + 2 * Math.PI * radius}
                y2={radius * 2}
                stroke="blue"
                strokeWidth="3"
              />
              <text
                x={20 + Math.PI * radius}
                y={radius * 2 + 25}
                textAnchor="middle"
                fill="blue"
                fontWeight="bold"
              >
                周长 = 2πr
              </text>
              <line
                x1={20}
                y1={radius * 2}
                x2={20}
                y2={radius}
                stroke="red"
                strokeWidth="3"
              />
              <text
                x={10}
                y={radius * 1.5}
                textAnchor="middle"
                fill="red"
                fontWeight="bold"
                transform="rotate(-90, 10, radius * 1.5)"
              >
                半径 = r
              </text>
            </g>
          )}

          {step >= 2 &&
            step < 5 &&
            showTriangles &&
            triangles.map((triangle, index) => (
              <polygon
                key={index}
                points={triangle.points}
                transform={`translate(20, 20)`}
                fill={`hsl(${(index * 360) / sides}, 70%, 80%)`}
                stroke="rgba(0,0,0,0.3)"
                strokeWidth="1"
              />
            ))}

          {(step === 2 || step === 3) && (
            <circle cx={radius + 20} cy={radius + 20} r={3} fill="red" />
          )}

          {step === 3 && (
            <line
              x1={radius + 20}
              y1={radius + 20}
              x2={radius + 20 + radius * Math.cos(Math.PI / sides)}
              y2={radius + 20 + radius * Math.sin(Math.PI / sides)}
              stroke="red"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          )}

          {step === 6 && showFormula && (
            <>
              <line
                x1={radius + 20}
                y1={radius + 20}
                x2={radius + 20 + radius}
                y2={radius + 20}
                stroke="red"
                strokeWidth="2"
              />
              <circle
                cx={radius + 20}
                cy={radius + 20}
                r={radius}
                fill="none"
                stroke="blue"
                strokeWidth="4"
                strokeDasharray="8,2"
              />
            </>
          )}
        </svg>

        {step === 5 && showFormula && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded">
            <div className="text-xl font-bold">S = πr² = 2πr × r ÷ 2</div>
            <div className="text-sm">周长 × 半径 ÷ 2</div>
          </div>
        )}
      </div>

      <div className="mb-4 w-full max-w-md">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          半径: {radius}像素
        </label>
        <input
          type="range"
          min="50"
          max="150"
          value={radius}
          onChange={(e) => setRadius(parseInt(e.target.value))}
          className="w-full"
        />
      </div>

      {step >= 1 && step < 5 && (
        <div className="mb-4 w-full max-w-md">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            边数: {sides}
          </label>
          <input
            type="range"
            min="3"
            max="50"
            value={sides}
            onChange={(e) => setSides(parseInt(e.target.value))}
            className="w-full"
          />
        </div>
      )}

      {step >= 2 && step < 5 && (
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={showTriangles}
              onChange={() => setShowTriangles(!showTriangles)}
              className="mr-2"
            />
            <span>显示三角形</span>
          </label>
        </div>
      )}

      {step === 5 && (
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={showFormula}
              onChange={() => setShowFormula(!showFormula)}
              className="mr-2"
            />
            <span>显示公式推导</span>
          </label>
        </div>
      )}

      <div className="flex space-x-4">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className={`px-4 py-2 rounded ${
            step === 0
              ? "bg-gray-300"
              : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
        >
          上一步
        </button>
        <button
          onClick={() => setStep(Math.min(5, step + 1))}
          disabled={step === 5}
          className={`px-4 py-2 rounded ${
            step === 5
              ? "bg-gray-300"
              : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
        >
          下一步
        </button>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 rounded-lg text-sm">
        <p>
          <strong>计算结果:</strong>
        </p>
        <p>圆的半径 = {radius} 像素</p>
        <p>圆的周长 = 2πr = {circumference.toFixed(2)} 像素</p>
        <p>圆的面积 = πr² = {area.toFixed(2)} 平方像素</p>
        {step >= 1 && step < 5 && (
          <p>
            正{sides}边形的面积 ≈{" "}
            {(
              sides *
              0.5 *
              radius *
              radius *
              Math.sin((2 * Math.PI) / sides)
            ).toFixed(2)}{" "}
            平方像素
          </p>
        )}
      </div>
    </div>
  );
};

export default CircleAreaTransformation;
